// SnapshotBrowser.tsx — Phase 4c: modal list of auto-snapshots with restore/delete
// Opened from TopBar or ProjectView

import { useState, useEffect, useCallback } from 'react';
import { useShallowStore } from '../store/useStore';
import type { SnapshotRecord } from '../store/useStore';

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function SnapshotBrowser({ open, onClose }: Props) {
  const { project, getSnapshots, restoreSnapshot, deleteSnapshot, createSnapshot } = useShallowStore(s => ({
    project: s.project,
    getSnapshots: s.getSnapshots,
    restoreSnapshot: s.restoreSnapshot,
    deleteSnapshot: s.deleteSnapshot,
    createSnapshot: s.createSnapshot,
  }));

  const [snaps, setSnaps] = useState<SnapshotRecord[]>([]);

  const refresh = useCallback(async () => {
    const list = await getSnapshots();
    setSnaps(list);
  }, [getSnapshots]);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  if (!open) return null;

  const maxSnaps = project?.settings?.maxSnapshots ?? 20;

  async function handleRestore(autoId: number) {
    await restoreSnapshot(autoId);
    onClose();
  }

  async function handleDelete(autoId: number) {
    await deleteSnapshot(autoId);
    refresh();
  }

  return (
    <div className="snapshot-overlay" onClick={onClose}>
      <div className="snapshot-modal" onClick={e => e.stopPropagation()}>
        <div className="snapshot-header">
          <h3>Project Snapshots</h3>
          <span className="snapshot-count">{snaps.length} / {maxSnaps}</span>
          <button className="snapshot-close" onClick={onClose}>×</button>
        </div>
        {snaps.length === 0 ? (
          <div className="snapshot-empty">
            <p>No snapshots yet. Snapshots are created automatically when switching modes.</p>
            <button
              className="snapshot-restore-btn"
              style={{ marginTop: 12 }}
              onClick={async () => { await createSnapshot(); refresh(); }}
              disabled={!project}
            >
              Save snapshot now
            </button>
          </div>
        ) : (
          <ul className="snapshot-list">
            {snaps.map(s => (
              <li key={s.autoId} className="snapshot-item">
                <span className="snapshot-time">
                  {new Date(s.createdAt).toLocaleString()}
                </span>
                <span className="snapshot-panels">
                  {s.data.panels.length} panel{s.data.panels.length !== 1 ? 's' : ''}
                </span>
                <button className="snapshot-restore-btn" onClick={() => handleRestore(s.autoId!)}>
                  Restore
                </button>
                <button className="snapshot-delete-btn" onClick={() => handleDelete(s.autoId!)}>
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
